/**
 * ShieldMaterial — sci-fi energy shield / force-field / hologram surface.
 *
 * The canonical composition (Cyanilux force-field breakdown family):
 *   · Fresnel rim — pow(1 − |N·V|, rimPower) edge glow
 *   · Scanlines — world-Y sine bands scrolling over time
 *   · Depth intersection highlight — where scene geometry nears the shield
 *     surface, glow rises: 1 − saturate((sceneZ − fragZ)/intersectDistance),
 *     via the same viewportLinearDepth mechanism as soft particles
 *   · Additive, double-sided, depth-read-no-write
 *
 * Returns the material + uniforms for runtime animation (hit pulses etc.).
 * Hologram preset: lower rim power, stronger scanlines, no intersection.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, normalView, positionViewDirection,
  positionWorld, time, sin, abs, pow, saturate,
  viewportLinearDepth, linearDepth,
} from 'three/tsl';

export interface ShieldConfig {
  /** Base colour (default cyan 0.2, 0.8, 1). */
  color?: [number, number, number];
  /** Rim exponent (default 3). */
  rimPower?: number;
  /** Overall intensity multiplier (default 1.5). */
  intensity?: number;
  /** Scanline spatial frequency per world unit (default 8). */
  scanFrequency?: number;
  /** Scanline scroll speed (default 1.5). */
  scanSpeed?: number;
  /** Scanline contribution 0..1 (default 0.25). */
  scanStrength?: number;
  /** Intersection glow colour (defaults to `color`). */
  intersectColor?: [number, number, number];
  /** World distance of the intersection band (default 0.4). */
  intersectDistance?: number;
  /** Camera far plane for depth linearization (default 1000). */
  cameraFar?: number;
}

export interface ShieldUniforms {
  color: { value: THREE.Vector3 };
  rimPower: { value: number };
  intensity: { value: number };
  scanFrequency: { value: number };
  scanSpeed: { value: number };
  scanStrength: { value: number };
  intersectColor: { value: THREE.Vector3 };
  intersectDistance: { value: number };
  cameraFar: { value: number };
}

/** CPU mirror of the fresnel rim term (exported for tests). */
export function fresnelRimCpu(nDotV: number, rimPower: number): number {
  const f = 1 - Math.abs(nDotV);
  return Math.pow(Math.max(f, 0), rimPower);
}

/** CPU mirror of the intersection glow (exported for tests). */
export function intersectGlowCpu(
  sceneLinear: number, fragLinear: number, intersectDistance: number, cameraFar: number,
): number {
  const deltaWorld = (sceneLinear - fragLinear) * cameraFar;
  const t = deltaWorld / Math.max(intersectDistance, 1e-6);
  const s = t < 0 ? 0 : (t > 1 ? 1 : t);
  return 1 - s;
}

export function createShieldMaterial(config?: ShieldConfig): {
  material: THREE.NodeMaterial; uniforms: ShieldUniforms;
} {
  const c = config?.color ?? [0.2, 0.8, 1];
  const ic = config?.intersectColor ?? c;
  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uRimPower = uniform(config?.rimPower ?? 3);
  const uIntensity = uniform(config?.intensity ?? 1.5);
  const uScanFreq = uniform(config?.scanFrequency ?? 8);
  const uScanSpeed = uniform(config?.scanSpeed ?? 1.5);
  const uScanStrength = uniform(config?.scanStrength ?? 0.25);
  const uIntersectColor = uniform(vec3(ic[0], ic[1], ic[2]));
  const uIntersectDist = uniform(config?.intersectDistance ?? 0.4);
  const uFar = uniform(config?.cameraFar ?? 1000);

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  material.blending = THREE.AdditiveBlending;

  material.colorNode = Fn(() => {
    // Fresnel rim — |N·V| so back faces rim identically (DoubleSide).
    const nDotV = abs(normalView.dot(positionViewDirection));
    const rim = pow(saturate(float(1).sub(nDotV)), uRimPower);
    // Scanlines — world-Y bands drifting upward.
    const scan = sin(positionWorld.y.mul(uScanFreq).sub(time.mul(uScanSpeed)))
      .mul(0.5).add(0.5).mul(uScanStrength);
    // Intersection highlight — geometry close behind the surface.
    const deltaWorld = viewportLinearDepth.sub(linearDepth()).mul(uFar);
    const intersect = float(1).sub(saturate(deltaWorld.div(uIntersectDist)));
    const body = uColor.mul(rim.add(scan));
    const glow = uIntersectColor.mul(intersect);
    const rgb = body.add(glow).mul(uIntensity);
    const alpha = saturate(rim.add(intersect).add(scan.mul(0.5)));
    return vec4(rgb, alpha);
  })();

  return {
    material: material as THREE.NodeMaterial,
    uniforms: {
      color: uColor as unknown as { value: THREE.Vector3 },
      rimPower: uRimPower as unknown as { value: number },
      intensity: uIntensity as unknown as { value: number },
      scanFrequency: uScanFreq as unknown as { value: number },
      scanSpeed: uScanSpeed as unknown as { value: number },
      scanStrength: uScanStrength as unknown as { value: number },
      intersectColor: uIntersectColor as unknown as { value: THREE.Vector3 },
      intersectDistance: uIntersectDist as unknown as { value: number },
      cameraFar: uFar as unknown as { value: number },
    },
  };
}

/** Hologram preset: soft rim, strong scanlines, no intersection band. */
export function createHologramMaterial(config?: Partial<ShieldConfig>): {
  material: THREE.NodeMaterial; uniforms: ShieldUniforms;
} {
  return createShieldMaterial({
    color: config?.color ?? [0.3, 0.9, 1],
    rimPower: config?.rimPower ?? 1.2,
    intensity: config?.intensity ?? 1.2,
    scanFrequency: config?.scanFrequency ?? 40,
    scanSpeed: config?.scanSpeed ?? 4,
    scanStrength: config?.scanStrength ?? 0.6,
    intersectDistance: config?.intersectDistance ?? 1e6, // effectively off
    cameraFar: config?.cameraFar,
    intersectColor: config?.intersectColor ?? [0, 0, 0],
  });
}
